/**
 * Cloudflare Turnstile — browser side.
 *
 * Headless by design: instead of every auth form plumbing a ref to a widget,
 * callers just `await requestTurnstileToken()`. The helper lazily injects the
 * Cloudflare script, renders a widget into a shared overlay container and
 * resolves with a token.
 *
 * With `appearance: "interaction-only"` the widget is invisible for normal
 * visitors — the overlay only becomes visible if Cloudflare decides a human
 * check is warranted, or when we ask for it explicitly after repeated failed
 * sign-ins.
 *
 * Never blocks sign-in: if the site key is unset, the script is blocked, or
 * Cloudflare times out, this resolves `null`/`skipped` and the caller proceeds
 * with the existing rate-limit + cooldown protections. UI subscribes to
 * `subscribeTurnstile` to show honest loading and failure states while this
 * happens.
 */

import { TURNSTILE_SITE_KEY_FALLBACK } from "@/config/turnstile-site-key";

export const TURNSTILE_SITE_KEY = (
  ((import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ||
    TURNSTILE_SITE_KEY_FALLBACK) ??
  ""
).trim();

export const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * Silent checks usually resolve in ~1s. The budget is generous because a
 * 3G-class mobile connection in Bangladesh can spend several seconds just
 * fetching the challenge assets, and writing that off as a failure pushes
 * ordinary customers onto the fallback path for no reason.
 */
export const SILENT_TIMEOUT_MS = 10000;
/** A visible challenge needs human time — never rush the visitor. */
export const INTERACTIVE_TIMEOUT_MS = 90000;

/** Escalate to a visible challenge once a device has failed this many sign-ins. */
export const TURNSTILE_ESCALATE_AFTER = 2;

export function turnstileConfigured(): boolean {
  return TURNSTILE_SITE_KEY.length > 0;
}

type RenderOptions = {
  sitekey: string;
  appearance?: "always" | "execute" | "interaction-only";
  execution?: "render" | "execute";
  theme?: "auto" | "light" | "dark";
  size?: "normal" | "flexible" | "compact";
  action?: string;
  retry?: "auto" | "never";
  callback?: (token: string) => void;
  "error-callback"?: (code?: string) => void;
  "timeout-callback"?: () => void;
  "expired-callback"?: () => void;
};

type TurnstileApi = {
  render: (el: HTMLElement, opts: RenderOptions) => string;
  execute: (id: string, opts?: Partial<RenderOptions>) => void;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/* ------------------------------------------------------------------ */
/* Observable status — powers the UI notice                            */
/* ------------------------------------------------------------------ */

export type TurnstileStatus =
  | "idle"
  /** Script loading / token being fetched silently. */
  | "checking"
  /** Cloudflare painted a visible challenge; the visitor must act. */
  | "interactive"
  /** Token obtained. */
  | "passed"
  /** Challenge failed or timed out; we continue with rate limits only. */
  | "failed"
  /** Not configured or the script is blocked — nothing to show. */
  | "unavailable";

export type TurnstileState = {
  status: TurnstileStatus;
  action: TurnstileAction | null;
  /** Machine-readable reason for `failed` / `unavailable`. */
  reason: TurnstileFailure | null;
};

export type TurnstileFailure =
  | "not_configured"
  | "script_blocked"
  | "timeout"
  | "challenge_error"
  /** Widget is not registered for this hostname / the key is wrong. */
  | "config_error";

let state: TurnstileState = { status: "idle", action: null, reason: null };
const listeners = new Set<(s: TurnstileState) => void>();

/**
 * True while a prewarm runs on page load. A background check must not paint a
 * "checking…" notice at a visitor who has not pressed anything yet — but if
 * Cloudflare paints a real challenge we still surface it.
 */
let backgroundRun = false;

function setState(next: Partial<TurnstileState>) {
  if (backgroundRun && next.status !== "interactive") return;
  state = { ...state, ...next };
  listeners.forEach((l) => l(state));
}

export function getTurnstileState(): TurnstileState {
  return state;
}

export function subscribeTurnstile(fn: (s: TurnstileState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Clear a lingering failure/passed badge (e.g. when the user edits the form). */
export function resetTurnstileState(): void {
  if (state.status === "idle") return;
  setState({ status: "idle", action: null, reason: null });
}

/* ------------------------------------------------------------------ */
/* Script loading                                                      */
/* ------------------------------------------------------------------ */

let scriptPromise: Promise<TurnstileApi | null> | null = null;

/** Injects the Turnstile script once. Resolves `null` when it can't load. */
export function loadTurnstile(timeoutMs = 8000): Promise<TurnstileApi | null> {
  if (typeof window === "undefined" || !turnstileConfigured()) return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi | null>((resolve) => {
    let settled = false;
    const finish = (api: TurnstileApi | null) => {
      if (settled) return;
      settled = true;
      if (!api) scriptPromise = null; // allow a retry on the next attempt
      resolve(api);
    };

    const timer = window.setTimeout(() => finish(null), timeoutMs);
    const done = () => {
      window.clearTimeout(timer);
      finish(window.turnstile ?? null);
    };

    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile="1"]');
    if (existing) {
      existing.addEventListener("load", done, { once: true });
      existing.addEventListener("error", () => {
        window.clearTimeout(timer);
        finish(null);
      });
      return;
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.dataset["turnstile"] = "1";
    script.addEventListener("load", done, { once: true });
    script.addEventListener("error", () => {
      window.clearTimeout(timer);
      finish(null);
    });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Warm the check up when the auth page mounts so the first submit is instant.
 *
 * With an `action` this goes further than loading the script: it runs the
 * whole silent check in the background and parks the resulting token, so the
 * very first submit no longer races an unfinished check (which is what
 * produced the "no pass" rows in the audit log).
 */
export function prewarmTurnstile(action?: TurnstileAction): void {
  if (!turnstileConfigured() || sessionUnavailable) return;
  void loadTurnstile().catch(() => null);
  if (!action) return;
  if (freshWarmToken() || pendingSilent) return;
  backgroundRun = true;
  pendingSilent = runVerify({ action }).finally(() => {
    backgroundRun = false;
    pendingSilent = null;
  });
  void pendingSilent.catch(() => null);
}

/* ------------------------------------------------------------------ */
/* Overlay host                                                        */
/* ------------------------------------------------------------------ */

const OVERLAY_ID = "jb-turnstile-overlay";

function ensureOverlay(): { overlay: HTMLElement; host: HTMLElement } {
  let overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = OVERLAY_ID;
    overlay.setAttribute("aria-live", "polite");
    overlay.setAttribute("role", "group");
    overlay.setAttribute("aria-label", "Security check");
    // Hidden until Cloudflare actually paints a challenge into the host.
    overlay.style.cssText = [
      "position:fixed",
      "inset:auto 0 0 0",
      "z-index:80",
      "display:none",
      "justify-content:center",
      "padding:16px",
      "pointer-events:none",
    ].join(";");
    const card = document.createElement("div");
    card.dataset["turnstileCard"] = "1";
    card.style.cssText = [
      "pointer-events:auto",
      "background:hsl(var(--card, 0 0% 100%))",
      "border:1px solid hsl(var(--border, 0 0% 88%))",
      "border-radius:12px",
      "padding:12px",
      "box-shadow:0 12px 32px rgb(0 0 0 / 0.18)",
      "max-width:min(420px, 100%)",
    ].join(";");
    const host = document.createElement("div");
    host.dataset["turnstileHost"] = "1";
    card.appendChild(host);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  }
  const host = overlay.querySelector<HTMLElement>('[data-turnstile-host="1"]')!;
  return { overlay, host };
}

function currentTheme(): "light" | "dark" {
  if (typeof document === "undefined") return "light";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/* ------------------------------------------------------------------ */
/* Token request                                                       */
/* ------------------------------------------------------------------ */

export type TurnstileAction =
  | "signin"
  | "signup"
  | "magiclink"
  | "recovery"
  | "otp_resend"
  | "grievance";

export type TurnstileRequest = {
  action: TurnstileAction;
  /** Force a visible challenge (used after repeated failed sign-ins). */
  interactive?: boolean;
  timeoutMs?: number;
};

export type TurnstileResult = {
  token: string | null;
  status: Extract<TurnstileStatus, "passed" | "failed" | "unavailable">;
  reason: TurnstileFailure | null;
};

/**
 * Cloudflare error codes that will never succeed on a retry: the widget is
 * misconfigured for this hostname/key. Escalating would only make the visitor
 * wait twice as long for the same outcome, so we stand down for the session.
 * https://developers.cloudflare.com/turnstile/reference/client-side-errors/
 */
function isFatalCode(code: string | undefined): boolean {
  if (!code) return false;
  // 110200 unknown domain, 110100/110110 invalid sitekey, 400020 bad params.
  return /^(1101|1102|4000)/.test(code);
}

/** Set once a fatal config error is seen — keeps later submits instant. */
let sessionUnavailable = false;

/** One widget render+execute cycle. Resolves the token or a failure reason. */
function runChallenge(
  api: TurnstileApi,
  {
    action,
    interactive,
    timeoutMs,
  }: Required<Omit<TurnstileRequest, "action">> & {
    action: TurnstileAction;
  },
): Promise<{ token: string | null; reason: TurnstileFailure | null; fatal: boolean }> {
  const { overlay, host } = ensureOverlay();
  host.innerHTML = "";

  return new Promise((resolve) => {
    let widgetId: string | undefined;
    let settled = false;
    let paintProbe = 0;

    const cleanup = () => {
      overlay.style.display = "none";
      try {
        if (widgetId) api.remove(widgetId);
      } catch {
        /* widget already gone */
      }
      host.innerHTML = "";
    };

    const finish = (token: string | null, reason: TurnstileFailure | null, fatal = false) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.clearTimeout(paintProbe);
      cleanup();
      resolve({ token, reason, fatal });
    };

    const timer = window.setTimeout(() => finish(null, "timeout"), timeoutMs);

    try {
      widgetId = api.render(host, {
        sitekey: TURNSTILE_SITE_KEY,
        appearance: interactive ? "always" : "interaction-only",
        execution: "execute",
        theme: currentTheme(),
        size: "flexible",
        action,
        retry: "never",
        callback: (token) => finish(token, null),
        "error-callback": (code) => {
          const fatal = isFatalCode(code);
          if (fatal && import.meta.env.DEV) {
            console.warn("[turnstile] widget misconfigured for this host", code);
          }
          finish(null, fatal ? "script_blocked" : "challenge_error", fatal);
        },
        "timeout-callback": () => finish(null, "timeout"),
        "expired-callback": () => finish(null, "timeout"),
      });
      // Show the overlay only once something is actually painted into it.
      overlay.style.display = "flex";
      paintProbe = window.setTimeout(() => {
        if (settled) return;
        if (host.clientHeight === 0) {
          overlay.style.display = "none";
        } else {
          // A real challenge is on screen — tell the UI to explain why.
          setState({ status: "interactive", action, reason: null });
        }
      }, 400);
      api.execute(widgetId);
    } catch {
      finish(null, "challenge_error");
    }
  });
}

/* ------------------------------------------------------------------ */
/* Warm token + in-flight sharing                                      */
/* ------------------------------------------------------------------ */

/** Cloudflare tokens live ~5 minutes; expire ours early with margin to spare. */
export const WARM_TOKEN_TTL_MS = 240_000;

let warmToken: { token: string; at: number } | null = null;
let pendingSilent: Promise<TurnstileResult> | null = null;

function freshWarmToken(): string | null {
  if (!warmToken) return null;
  if (Date.now() - warmToken.at > WARM_TOKEN_TTL_MS) {
    warmToken = null;
    return null;
  }
  return warmToken.token;
}

/** Tokens are single-use: hand it out once, then forget it. */
function takeWarmToken(): string | null {
  const t = freshWarmToken();
  warmToken = null;
  return t;
}

/**
 * Full verification with a single graceful retry: if a silent check errors or
 * times out, we escalate once to a visible challenge before giving up. Never
 * rejects, and never keeps the visitor waiting on a check that cannot pass.
 *
 * A silent request reuses a token parked by `prewarmTurnstile()`, or joins a
 * check that is already running, instead of starting a second one.
 */
export async function verifyHuman(req: TurnstileRequest): Promise<TurnstileResult> {
  const { action, interactive = false } = req;

  if (!interactive) {
    const warm = takeWarmToken();
    if (warm) {
      setState({ status: "passed", action, reason: null });
      return { token: warm, status: "passed", reason: null };
    }
    if (pendingSilent) {
      // A prewarm is mid-flight — wait for it rather than racing it.
      backgroundRun = false;
      setState({ status: "checking", action, reason: null });
      const shared = await pendingSilent;
      if (shared.token) {
        warmToken = null;
        setState({ status: "passed", action, reason: null });
        return shared;
      }
      // The warm attempt produced nothing; fall through to a fresh attempt.
    }
  }

  return runVerify(req);
}

async function runVerify({
  action,
  interactive = false,
  timeoutMs = interactive ? INTERACTIVE_TIMEOUT_MS : SILENT_TIMEOUT_MS,
}: TurnstileRequest): Promise<TurnstileResult> {
  if (!turnstileConfigured()) {
    setState({ status: "unavailable", action, reason: "not_configured" });
    return { token: null, status: "unavailable", reason: "not_configured" };
  }
  if (sessionUnavailable) {
    setState({ status: "unavailable", action, reason: "config_error" });
    return { token: null, status: "unavailable", reason: "config_error" };
  }

  setState({ status: "checking", action, reason: null });

  const api = await loadTurnstile();
  if (!api) {
    setState({ status: "unavailable", action, reason: "script_blocked" });
    return { token: null, status: "unavailable", reason: "script_blocked" };
  }

  let attempt = await runChallenge(api, { action, interactive, timeoutMs });

  // Escalate once only when Cloudflare actively errored — a silent *timeout*
  // means the check is not going to resolve, and making the visitor wait a
  // second full round for the same outcome is worse than falling back to the
  // server-side rate limiter.
  if (!attempt.token && !attempt.fatal && !interactive && attempt.reason === "challenge_error") {
    setState({ status: "interactive", action, reason: null });
    attempt = await runChallenge(api, {
      action,
      interactive: true,
      timeoutMs: INTERACTIVE_TIMEOUT_MS,
    });
  }

  if (attempt.token) {
    // A prewarm parks its token for the submit that follows; a real submit
    // consumes it immediately (tokens are single-use).
    if (backgroundRun) warmToken = { token: attempt.token, at: Date.now() };
    setState({ status: "passed", action, reason: null });
    return { token: attempt.token, status: "passed", reason: null };
  }

  if (attempt.fatal) {
    // Misconfigured widget (usually: this hostname is not on the site key's
    // allow-list). Stop challenging for the rest of the session and fall back
    // to rate limiting instead of nagging the visitor every submit. The
    // visitor sees nothing; we leave a breadcrumb for whoever fixes the key.
    sessionUnavailable = true;
    if (typeof console !== "undefined") {
      console.warn("[turnstile] disabled for this session — widget not valid for", {
        hostname: typeof location !== "undefined" ? location.hostname : "unknown",
      });
    }
    setState({ status: "unavailable", action, reason: "config_error" });
    return { token: null, status: "unavailable", reason: "config_error" };
  }

  const reason = attempt.reason ?? "challenge_error";
  setState({ status: "failed", action, reason });
  return { token: null, status: "failed", reason };
}

/**
 * Back-compat convenience wrapper: resolves a token, or `null` when Turnstile
 * is unavailable or the challenge failed.
 */
export async function requestTurnstileToken(req: TurnstileRequest): Promise<string | null> {
  return (await verifyHuman(req)).token;
}
